import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  Flex,
  Button,
  SimpleGrid,
  HStack
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { AppContext } from './AppContext';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const MPHCodingQualityDashboard = () => {
  const { accessToken } = useContext(AppContext);
  
  const [loading, setLoading] = useState(true);
  const [triageStats, setTriageStats] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    if (!accessToken) return;
    
    setLoading(true);
    setError(null);
    try {
      const triageResponse = await callApi('/mph/triage-stats', accessToken, { method: 'GET' });
      
      if (triageResponse.ok) {
        const triageResult = await triageResponse.json();
        if (triageResult.status === 'success') {
          setTriageStats(triageResult.data);
        } else {
          setError(triageResult.message || 'Failed to fetch triage stats');
        }
      } else {
        setError(`API Error: ${triageResponse.status}`);
      }
    } catch (err) {
      console.error("Error fetching MPH data:", err);
      setError('An error occurred while fetching data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  return (
    <Box>
      <HeaderMenus />
      <Box p={8} maxWidth="1400px" margin="0 auto">
        <Flex justify="flex-end" align="center" mb={6}>
          <HStack spacing={4}>
            <Button as={RouterLink} to="/mph-guidelines" colorScheme="blue" variant="outline" size="sm">
              View Coding Guidelines
            </Button>
            <Button as="a" href="/mph-sa-quality" colorScheme="blue" variant="outline" size="sm">
              View Surgical Assist Cases
            </Button>
          </HStack>
        </Flex>

        {error && (
          <Box bg="red.100" color="red.700" p={3} borderRadius="md" mb={4}>
            {error}
          </Box>
        )}

        {loading ? (
          <Flex justify="center" p={10}>
            <Spinner size="xl" />
          </Flex>
        ) : (
          <>
            {triageStats ? (
              <Box>
                {/* Summary Cards */}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
                  <Box bg="white" p={6} borderRadius="md" shadow="sm" borderWidth="1px" textAlign="center">
                    <Text color="gray.500" fontSize="sm" fontWeight="bold" textTransform="uppercase">Total Triaged Cases</Text>
                    <Heading size="xl" color="blue.600" mt={2}>{triageStats.total_cases}</Heading>
                  </Box>
                  <Box bg="white" p={6} borderRadius="md" shadow="sm" borderWidth="1px" textAlign="center">
                    <Text color="gray.500" fontSize="sm" fontWeight="bold" textTransform="uppercase">Surgical Assist (Clinical)</Text>
                    <Heading size="xl" color="green.500" mt={2}>{triageStats.case_types['Clinical'] || 0}</Heading>
                  </Box>
                </SimpleGrid>

                {/* Daily Volume Chart */}
                <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px" mb={6}>
                  <Heading size="sm" mb={4} color="gray.600">Case Volume by Date of Procedure</Heading>
                  <Box height="300px">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={triageStats.daily_volume} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="date" tick={{fontSize: 12}} />
                        <YAxis tick={{fontSize: 12}} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        <Bar dataKey="with_op" name="With OP Report" stackId="a" fill="#38A169" />
                        <Bar dataKey="without_op" name="Without OP Report" stackId="a" fill="#3182ce" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>

                {/* Tables */}
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                  <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px">
                    <Heading size="sm" mb={4} color="gray.600">Top Surgeons</Heading>
                    <Box overflowY="auto" maxHeight="400px">
                      <Table variant="simple" size="sm">
                        <Thead position="sticky" top={0} bg="white" zIndex={1}>
                          <Tr>
                            <Th>Surgeon</Th>
                            <Th isNumeric>Cases</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {triageStats.top_surgeons.map((s, i) => (
                            <Tr key={i}>
                              <Td>{s.name}</Td>
                              <Td isNumeric fontWeight="bold">{s.count}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>

                  <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px">
                    <Heading size="sm" mb={4} color="gray.600">Top Facilities</Heading>
                    <Box overflowY="auto" maxHeight="400px">
                      <Table variant="simple" size="sm">
                        <Thead position="sticky" top={0} bg="white" zIndex={1}>
                          <Tr>
                            <Th>Facility</Th>
                            <Th isNumeric>Cases</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {triageStats.top_facilities.map((f, i) => (
                            <Tr key={i}>
                              <Td>{f.name}</Td>
                              <Td isNumeric fontWeight="bold">{f.count}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                </SimpleGrid>
              </Box>
            ) : (
              <Box textAlign="center" py={10}>
                <Text color="gray.500">Loading triage statistics...</Text>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default MPHCodingQualityDashboard;
